import { useMemo } from 'react'
import moment from 'moment'
import type { UserData, DisplayEntry, ActivityType } from '../../types'
import { restructure, getEntriesActivityDesc } from '../../utils/entries'
import { intakeTotal } from '../../utils/intake'
import { timeToSeconds, toHHMMSS, diffHuman, DATETIME_FORMAT } from '../../utils/calc'

const num = (v: unknown): number => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

type Trend = 'up' | 'down' | 'same'
function trend(current: number, previous: number): Trend {
  if (current > previous) return 'up'
  if (previous > current) return 'down'
  return 'same'
}
const trendClass = (t: Trend) =>
  t === 'up' ? 'text-[var(--bac-safe)]' : t === 'down' ? 'text-[var(--bac-danger)]' : 'text-[var(--text-secondary)]'
const trendSign = (t: Trend) => (t === 'up' ? '+' : t === 'down' ? '−' : '')

interface CardioLike {
  calories: number
  duration: number
  distance: number
  level: string
}

function get(e: DisplayEntry, k: string): string {
  return (e as unknown as Record<string, string>)[k] || ''
}

export default function WorkoutReport({ data }: { data: UserData }) {
  const model = useMemo(() => buildReport(data), [data])

  if (!model) {
    return (
      <div className="card text-center text-[var(--text-secondary)] mt-4">
        Log an activity to see your daily report.
      </div>
    )
  }

  const { date, hasMoves, nutrition, duration, totalCalories, calTrend, calDiff, totalKg, kgDisplay, kgLabel, kgTrend, kgDiff, cardio, weights, exercise } =
    model

  return (
    <div className="mt-4 space-y-4">
      <h2 className="text-lg font-bold">
        Daily report <span className="text-[var(--text-secondary)] font-normal">— {date}</span>
      </h2>
      {hasMoves && duration !== '00:00:00' && (
        <p className="text-sm text-[var(--text-secondary)]">Total logged move time: {duration}.</p>
      )}

      {nutrition && (
        <section>
          <h3 className="font-semibold mb-2">Food &amp; drink</h3>
          <div className="card">
            <div className="text-sm">
              <span className="font-medium text-[var(--text-primary)]">{Math.round(nutrition.calories)} kcal</span>
              <span className="text-[var(--text-secondary)]">
                {' · '}Protein {Math.round(nutrition.protein)}g · Carbs {Math.round(nutrition.carbs)}g · Fat{' '}
                {Math.round(nutrition.fat)}g
              </span>
            </div>
            <div className="mt-1 text-xs text-[var(--text-secondary)]">
              {nutrition.items.map((i) => (i.servings > 1 ? `${i.name} ×${i.servings}` : i.name)).join(', ')}
            </div>
          </div>
        </section>
      )}

      <div className="grid grid-cols-2 gap-3">
        {totalCalories > 0 && (
          <div className="card text-center">
            <div className="text-xs uppercase tracking-wide text-[var(--text-secondary)]">Calories</div>
            <div className={`text-3xl font-bold ${trendClass(calTrend)}`}>
              {Math.round(totalCalories).toLocaleString('en')}
            </div>
            {calDiff !== 0 && (
              <div className={`text-xs ${trendClass(calTrend)}`}>
                {trendSign(calTrend)}
                {Math.round(calDiff).toLocaleString('en')}
              </div>
            )}
          </div>
        )}
        {totalKg > 0 && (
          <div className="card text-center">
            <div className="text-xs uppercase tracking-wide text-[var(--text-secondary)]">{kgLabel}</div>
            <div className={`text-3xl font-bold ${trendClass(kgTrend)}`}>{kgDisplay}</div>
            {kgDiff !== 0 && (
              <div className={`text-xs ${trendClass(kgTrend)}`}>
                {trendSign(kgTrend)}
                {Math.round(kgDiff).toLocaleString('en')}
              </div>
            )}
          </div>
        )}
      </div>

      {cardio.length > 0 && (
        <section>
          <h3 className="font-semibold mb-2">Cardio</h3>
          {cardio.map((c) => (
            <div key={c.name} className="card mb-2">
              <h4 className="font-medium mb-1">{c.name}</h4>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                <Metric value={`${Math.round(c.cur.calories)} cal`} t={c.calT} d={Math.round(c.calD)} />
                <Metric value={`${c.cur.distance.toFixed(2)} km`} t={c.distT} d={c.distD.toFixed(2)} />
                <Metric value={toHHMMSS(c.cur.duration)} t={c.durT} d={`${Math.round(c.durD)}s`} />
              </div>
            </div>
          ))}
        </section>
      )}

      {weights.length > 0 && (
        <section>
          <h3 className="font-semibold mb-2">Weights</h3>
          {weights.map((w) => (
            <div key={w.name} className="card mb-2">
              <h4 className="font-medium mb-1">{w.name}</h4>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                {w.sets.map((s, i) => (
                  <span key={i} className={trendClass(s.t)}>
                    {s.kg}
                    <span className="text-xs">kg</span> ×{s.reps}{' '}
                    {s.d !== 0 && <span className="text-xs">{trendSign(s.t)}{Math.round(Math.abs(s.d))}</span>}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </section>
      )}

      {exercise.length > 0 && (
        <section>
          <h3 className="font-semibold mb-2">Exercise</h3>
          {exercise.map((c) => (
            <div key={c.name} className="card mb-2">
              <h4 className="font-medium mb-1">{c.name}</h4>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                {c.cur.level && <span className="text-[var(--text-secondary)]">level: {c.cur.level}</span>}
                <Metric value={`${Math.round(c.cur.calories)} cal`} t={c.calT} d={Math.round(c.calD)} />
                <Metric value={`${c.cur.distance.toFixed(2)} km`} t={c.distT} d={c.distD.toFixed(2)} />
                <Metric value={toHHMMSS(c.cur.duration)} t={c.durT} d={`${Math.round(c.durD)}s`} />
              </div>
            </div>
          ))}
        </section>
      )}
    </div>
  )
}

function Metric({ value, t, d }: { value: string; t: Trend; d: number | string }) {
  return (
    <span className={trendClass(t)}>
      {value} {Number(d) !== 0 && <span className="text-xs">{trendSign(t)}{typeof d === 'number' ? Math.abs(d) : d}</span>}
    </span>
  )
}

// ---- model building ------------------------------------------------------

function summarizeCardioLike(entries: DisplayEntry[], type: ActivityType): Map<string, CardioLike> {
  const map = new Map<string, CardioLike>()
  for (const e of entries) {
    if (e.type !== type) continue
    const name = e.displayName
    const cur = map.get(name) || { calories: 0, duration: 0, distance: 0, level: '' }
    cur.calories += num(get(e, 'calories'))
    cur.duration += get(e, 'duration') ? timeToSeconds(get(e, 'duration')) : 0
    cur.distance += num(get(e, 'distance'))
    cur.level = get(e, 'level') || get(e, 'intensity') || cur.level
    map.set(name, cur)
  }
  return map
}

function priorCardioLike(data: UserData, type: ActivityType, name: string, before: string): CardioLike {
  const prior = getEntriesActivityDesc(data, type, name, 1, before)[0]
  if (!prior) return { calories: 0, duration: 0, distance: 0, level: '' }
  return {
    calories: num(get(prior as unknown as DisplayEntry, 'calories')),
    duration: get(prior as unknown as DisplayEntry, 'duration')
      ? timeToSeconds(get(prior as unknown as DisplayEntry, 'duration'))
      : 0,
    distance: num(get(prior as unknown as DisplayEntry, 'distance')),
    level: '',
  }
}

function buildReport(data: UserData) {
  const grouped = restructure(data)
  const intakeDates = (data.intake ?? []).map((it) =>
    moment(it.datetime, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD'),
  )
  // Most recent day with *any* activity — a move or food/drink.
  const dates = Array.from(new Set([...Object.keys(grouped), ...intakeDates])).sort()
  if (dates.length === 0) return null

  const dateKey = dates[dates.length - 1]
  const priorKey = dates[dates.length - 2]
  const dayEntries = grouped[dateKey] || []
  const priorEntries = priorKey ? grouped[priorKey] || [] : []

  // Food & drink logged on the report's day.
  const dayIntake = (data.intake ?? []).filter(
    (it) => moment(it.datetime, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD') === dateKey,
  )
  let nutrition:
    | { calories: number; protein: number; carbs: number; fat: number; items: { name: string; servings: number }[] }
    | null = null
  if (dayIntake.length) {
    const t = dayIntake.reduce(
      (acc, it) => {
        const p = intakeTotal(it)
        acc.calories += p.calories
        acc.protein += p.protein || 0
        acc.carbs += p.carbs || 0
        acc.fat += p.fat || 0
        return acc
      },
      { calories: 0, protein: 0, carbs: 0, fat: 0 },
    )
    nutrition = { ...t, items: dayIntake.map((it) => ({ name: it.name || '—', servings: it.servings || 1 })) }
  }

  // Day duration: span between first and last entry, else the single duration.
  const times = dayEntries.map((e) => e.datetime).sort()
  let duration = '00:00:00'
  if (times.length > 1 && times[0] !== times[times.length - 1]) {
    duration = moment
      .utc(moment(times[times.length - 1]).diff(moment(times[0])))
      .format('HH:mm:ss')
  } else if (dayEntries[0]) {
    duration = get(dayEntries[0], 'duration') || '00:00:00'
  }

  const sumCalories = (es: DisplayEntry[]) => es.reduce((t, e) => t + num(get(e, 'calories')), 0)
  const sumKg = (es: DisplayEntry[]) =>
    es.reduce((t, e) => (e.type === 'weights' ? t + num(get(e, 'kg')) * num(get(e, 'repetitions')) : t), 0)

  const totalCalories = sumCalories(dayEntries)
  const priorCalories = sumCalories(priorEntries)
  const totalKg = sumKg(dayEntries)
  const priorKg = sumKg(priorEntries)

  let kgLabel = 'kilograms'
  let kgDisplay = Math.round(totalKg).toLocaleString('en')
  if (totalKg > 10000) {
    kgLabel = 'tonnes'
    kgDisplay = (totalKg / 1000).toFixed(2)
  }

  // Cardio + exercise per activity with prior comparison.
  const buildCardioLike = (type: ActivityType) =>
    Array.from(summarizeCardioLike(dayEntries, type).entries()).map(([name, cur]) => {
      const prior = priorCardioLike(data, type, name, dateKey)
      return {
        name,
        cur,
        calT: trend(cur.calories, prior.calories),
        calD: cur.calories - prior.calories,
        distT: trend(cur.distance, prior.distance),
        distD: cur.distance - prior.distance,
        durT: trend(cur.duration, prior.duration),
        durD: cur.duration - prior.duration,
      }
    })

  // Weights per activity: per-set comparison vs the most recent prior session.
  const weightsMap = new Map<string, { kg: number; reps: number }[]>()
  for (const e of dayEntries) {
    if (e.type !== 'weights') continue
    const arr = weightsMap.get(e.displayName) || []
    arr.push({ kg: num(get(e, 'kg')), reps: num(get(e, 'repetitions')) })
    weightsMap.set(e.displayName, arr)
  }
  const weights = Array.from(weightsMap.entries()).map(([name, sets]) => {
    const prior = getEntriesActivityDesc(data, 'weights', name, 5, dateKey)
    return {
      name,
      sets: sets.map((s, i) => {
        const priorKgVal = prior[i] ? num((prior[i] as unknown as Record<string, string>).kg) : 0
        return { kg: s.kg, reps: s.reps, t: trend(s.kg, priorKgVal), d: s.kg - priorKgVal }
      }),
    }
  })

  return {
    date: diffHuman(moment(dateKey, 'YYYY-MM-DD').format(DATETIME_FORMAT), false),
    hasMoves: dayEntries.length > 0,
    nutrition,
    duration,
    totalCalories,
    calTrend: trend(totalCalories, priorCalories),
    calDiff: totalCalories - priorCalories,
    totalKg,
    kgDisplay,
    kgLabel,
    kgTrend: trend(totalKg, priorKg),
    kgDiff: totalKg - priorKg,
    cardio: buildCardioLike('cardio'),
    weights,
    exercise: buildCardioLike('exercise'),
  }
}
